import { useState, useCallback, useEffect, useRef } from 'react';
import { UserPlus, Trash2 } from 'lucide-react';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import { useAddSpaceMembers } from '../../hooks/queries/useSpaceQueries';
import { ISpace, ISpaceMember, IUser } from '../../types';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import UserAutocomplete from '../UserAutocomplete';

interface IAddMemberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  space: ISpace;
}

interface IMemberEntry {
  userId: string;
  displayName: string;
  username: string;
  avatar?: string | null;
}

const AVATAR_COLOR = '#3b82f6';

const AddMemberDialog = ({ isOpen, onClose, space }: IAddMemberDialogProps) => {
  const { t } = useTranslation();
  const [pendingMembers, setPendingMembers] = useState<IMemberEntry[]>([]);
  const [existingMemberIds, setExistingMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const addMembers = useAddSpaceMembers();

  // Load existing member IDs when dialog opens
  useEffect(() => {
    if (!isOpen) return;

    api
      .get<ISpaceMember[]>(`/backoffice/spaces/${space.id}/members`)
      .then((members) => {
        setExistingMemberIds(new Set(members.map((m) => m.userId)));
      })
      .catch(() => setExistingMemberIds(new Set()));
  }, [isOpen, space.id]);

  const pendingUserIds = pendingMembers.map((m) => m.userId);

  const handleRemoveUser = (userId: string) => {
    setPendingMembers((prev) => prev.filter((m) => m.userId !== userId));
  };

  // Cache of users returned by autocomplete searches
  const usersCache = useRef<Map<string, IMemberEntry>>(new Map());

  const fetchAllUsers = useCallback(
    async (search?: string) => {
      const params = new URLSearchParams();

      if (search) params.append('search', search);
      params.append('limit', '20');

      const result = await api.get<{ data: IUser[] }>(
        `/backoffice/users?${params.toString()}`,
      );

      const filtered = (result.data || [])
        .filter(
          (u) => !pendingUserIds.includes(u.id) && !existingMemberIds.has(u.id),
        )
        .map((u) => ({
          id: u.id,
          username: u.username,
          displayName: u.displayName || u.username,
          avatar: u.avatar,
        }));

      // Cache all fetched users for selection lookup
      for (const u of filtered) {
        usersCache.current.set(u.id, {
          userId: u.id,
          displayName: u.displayName,
          username: u.username,
          avatar: u.avatar,
        });
      }

      return filtered;
    },
    [pendingUserIds, existingMemberIds],
  );

  const handleSelectUser = (userId: string) => {
    if (!userId || pendingUserIds.includes(userId)) return;

    const cached = usersCache.current.get(userId);

    if (cached) {
      setPendingMembers((prev) => [...prev, cached]);
    }

    setAutocompleteKey((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    if (pendingMembers.length === 0) return;
    setError(null);

    try {
      await addMembers.mutateAsync({
        spaceId: space.id,
        userIds: pendingUserIds,
      });
      setPendingMembers([]);
      onClose();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleClose = () => {
    setPendingMembers([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>{t('spaces.add_member_title')}</DialogTitle>
          <DialogDescription>
            {t('spaces.add_member_desc', { name: space.name })}
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {/* User search autocomplete */}
        <div>
          <label className="block text-sm font-semibold text-foreground mb-2">
            {t('spaces.select_users')}
          </label>
          <UserAutocomplete
            key={autocompleteKey}
            value=""
            onChange={handleSelectUser}
            fetchUsers={fetchAllUsers}
            placeholder={t('spaces.search_users')}
            triggerPlaceholder={t('spaces.select_user_placeholder')}
          />
        </div>

        {/* Pending members list */}
        <div className="border border-border rounded-lg divide-y divide-border max-h-60 overflow-y-auto">
          {pendingMembers.length > 0 ? (
            pendingMembers.map((member) => (
              <div
                key={member.userId}
                className="flex items-center gap-3 px-4 py-3"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0"
                  style={{ backgroundColor: AVATAR_COLOR }}
                >
                  {member.displayName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {member.displayName}
                  </div>
                  <div className="text-xs text-gray-500">
                    @{member.username}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveUser(member.userId)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-400 p-4 text-center">
              {t('spaces.no_members_selected')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={pendingMembers.length === 0 || addMembers.isPending}
          >
            <UserPlus className="w-4 h-4 mr-1" />
            {addMembers.isPending
              ? t('common.saving')
              : t('spaces.confirm_add_members', {
                  count: pendingMembers.length,
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddMemberDialog;
